import {faEdit, faTag, faTimes} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {boundMethod} from "autobind-decorator";
import React from "react";
import {Link} from "react-router-dom";

import http from "@/services/http";
import {ERoles} from "@/services/models";
import session from "@/services/session";

import T from "@translate/T";
import SaveButton from "../button-like/SaveButton";
import Card from "../design/Card";

export interface ITagsEditorProps {
    project: number;
    value: string[];
    endpoint?: string;

    // only use this if not related to user rights.
    readonly?: boolean;

    onChanged(value: string[]): void;
}

interface ITagsEditorState {
    text: string;
    isEditMode: boolean;
}

class TagsEditor extends React.PureComponent<
    ITagsEditorProps,
    ITagsEditorState
> {
    public readonly state: ITagsEditorState = {isEditMode: false, text: ""};

    private readonly saveButton = React.createRef<SaveButton>();

    private get readonly() {
        const {project, readonly} = this.props;

        return (
            readonly ||
            (!session.hasRole(ERoles.Manager, project) &&
                !session.hasRole(ERoles.Editor, project))
        );
    }

    @boundMethod
    public setInputRef(input: HTMLInputElement) {
        if (input) {
            input.focus();
        }
    }

    @boundMethod
    public showEditor(e: React.SyntheticEvent) {
        e.preventDefault();

        if (this.readonly) {
            return;
        }

        const text = this.props.value.join(" ");
        this.setState({isEditMode: true, text});
    }

    @boundMethod
    public setText(e: React.ChangeEvent<HTMLInputElement>) {
        e.preventDefault();

        this.setState({text: e.target.value});
    }

    @boundMethod
    public onKeyDown(e: React.KeyboardEvent) {
        switch (e.key) {
            case "Escape":
                this.cancel(e);
                break;

            case "Enter":
                e.preventDefault();
                (this.saveButton.current?.pressedSave ?? this.saveChanges)();
                break;

            default:
                break;
        }
    }

    @boundMethod
    public async saveChanges() {
        const tags = this.state.text
            .trim()
            .split(/[\s,]+/)
            .filter(
                (tag, index, all) => tag.length && all.indexOf(tag) === index,
            );

        try {
            const {endpoint, onChanged} = this.props;
            if (endpoint) {
                const response = await http.post(endpoint, {json: {tags}});
                if (!response.ok) {
                    throw new http.HTTPError(response);
                }
            }

            onChanged(tags);
            this.setState({isEditMode: false});

            return true;
        } catch {
            return false;
        }
    }

    @boundMethod
    public cancel(e: React.SyntheticEvent) {
        e.preventDefault();

        this.setState({isEditMode: false});
    }

    public render() {
        return (
            <Card
                title={<T>Tags</T>}
                renderButtons={this.renderButtons}
                headerClassName="bg-secondary"
            >
                <div className="card-body">{this.renderBody()}</div>
            </Card>
        );
    }

    @boundMethod
    private renderButtons() {
        const {endpoint} = this.props;
        const {isEditMode} = this.state;

        if (this.readonly) {
            return null;
        }

        if (!isEditMode) {
            return (
                <button
                    type="button"
                    id="edit-tags"
                    className="btn btn-sm btn-secondary"
                    aria-label="edit-tags"
                    onClick={this.showEditor}
                >
                    <FontAwesomeIcon icon={faEdit} fixedWidth={true} />
                </button>
            );
        }

        if (endpoint) {
            return (
                <React.Fragment>
                    <SaveButton
                        ref={this.saveButton}
                        type="button"
                        id="save-tags"
                        classNameButton="btn-sm"
                        form="tags-editor"
                        onSave={this.saveChanges}
                    />

                    <button
                        type="button"
                        id="cancel-tags"
                        className="btn btn-sm btn-secondary"
                        onClick={this.cancel}
                    >
                        <FontAwesomeIcon
                            icon={faTimes}
                            fixedWidth={true}
                            className="mr-1"
                        />
                        <T>Cancel</T>
                    </button>
                </React.Fragment>
            );
        }

        return null;
    }

    private renderBody() {
        const {isEditMode} = this.state;

        if (!isEditMode) {
            return this.renderTags();
        }

        return this.renderTagsInput();
    }

    private renderTags() {
        const {project, value} = this.props;

        if (value.length) {
            const prefix = `/project/${project}?q=`;
            const links = value.map((x) => (
                <li key={x} className="list-inline-item">
                    <FontAwesomeIcon icon={faTag} fixedWidth={true} />
                    <Link to={prefix + encodeURIComponent("tag:" + x)}>
                        {x}
                    </Link>
                </li>
            ));

            return (
                <ul id="tags" className="list-inline" onClick={this.showEditor}>
                    {links}
                </ul>
            );
        }

        // no tag
        if (!this.readonly) {
            return (
                <div
                    id="no-tags"
                    className="pointer-cursor"
                    onClick={this.showEditor}
                >
                    <em className="text-muted">
                        <T>No tag set, click to edit.</T>
                    </em>
                </div>
            );
        }

        // no tag, readonly
        return (
            <div id="tags">
                <em className="text-muted">
                    <T>No tag set.</T>
                </em>
            </div>
        );
    }

    private renderTagsInput() {
        const {endpoint} = this.props;
        const {text} = this.state;

        return (
            <input
                type="text"
                id="tags-input"
                aria-label="tags-input"
                autoComplete="off"
                className="form-control"
                value={text}
                ref={this.setInputRef}
                onBlur={!endpoint ? this.saveChanges : undefined}
                onChange={this.setText}
                onKeyDown={this.onKeyDown}
            />
        );
    }
}

export default TagsEditor;
